/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, {useContext, useEffect, useState} from "react";
import Stockchart from "../stockchart_plotly";
import DisplayCards from "../metricCards";
import DynamicStockCharts from "../dynamicStockCharts_plotly";
import FundaTeaser from "../fundamentals/stockFundamentalTeaser";
import Plot from "react-plotly.js";
import {fetchMacros, fetchMarketIndices, fetchMarketSructure, fetchPage} from "../../api";
import MarketBreadth from "./market_breadth";
import SectorPerformance from "./sector_performance";
import LiveMarketOverview from "../marketsnap";
import Chartgrid from "../chartgrid";
import { generateMarketCommentary } from "./generateMarketCommentary";

const RollingSpreadChart = ({ spreads }) => {
  const dates = spreads.map(entry => entry.date);
  const nearMid = spreads.map(entry => entry.near_mid_spread);
  const midFar = spreads.map(entry => entry.mid_far_spread);

  return (
    // <div style={styles.container}>
        <div style={styles.chartBox}>
        <h2 className="text-xl font-semibold mb-2">NIFTY Rolling Futures Spread</h2>
      <Plot
        data={[
          {
            x: dates,
            y: nearMid,
            type: 'scatter',
            mode: 'lines+markers',
            name: 'Near-Mid Spread',
            line: { color: 'blue' },
          },
          {
            x: dates,
            y: midFar,
            type: 'scatter',
            mode: 'lines+markers',
            name: 'Mid-Far Spread',
            line: { color: 'green' },
          },
        ]}
        layout={{
          title: '(Near-Mid vs Mid-Far)',
          height : 600,
          // width : 1000,
          xaxis: { title: 'Date' },
          yaxis: { title: 'Spread (pts)' },
          legend: { orientation: 'v'},
          margin: { t: 40, b: 60 },
        }}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
    // </div>
  );
};

const ClientLongShortTrendChart = ({chartData}) => {

  const clients = [...new Set(chartData.map((item) => item.client))];

  const traces = clients.flatMap((client) => {
    const clientData = chartData.filter((d) => d.client === client);
    return [
      {
        x: clientData.map((d) => d.oi_date),
        y: clientData.map((d) => d.total_long_oi),
        type: "scatter",
        mode: "lines+markers",
        name: `${client} - Long`,
        line: { dash: "solid" }
      },
      {
        x: clientData.map((d) => d.oi_date),
        y: clientData.map((d) => d.total_short_oi),
        type: "scatter",
        mode: "lines+markers",
        name: `${client} - Short`,
        line: { dash: "dot" }
      }
    ];
  });

  return (
      <div style={styles.chartBox}>
      <h2 className="text-xl font-bold mb-4">Client-Wise Long vs Short Trend</h2>
      <Plot
        data={traces}
        layout={{
          title: "Long vs Short Positions Over Time",
          xaxis: { title: "Date" },
          yaxis: { title: "Open Interest" },
          legend: { orientation: "v" },
        }}
        useResizeHandler
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};


const ClientLongShortBarChart = ({barData}) => {

  const clients = barData.map((d) => d.client);
  const longOi = barData.map((d) => d.total_long_oi);
  const shortOi = barData.map((d) => d.total_short_oi);

  return (
      <div style={styles.chartBox}>
      <h2 className="text-xl font-bold mb-4">Current Long vs Short Positions (Latest Date)</h2>
      <Plot
        data={[
          {
            x: clients,
            y: longOi,
            type: "bar",
            name: "Long OI",
            marker: { color: "green" },
          },
          {
            x: clients,
            y: shortOi,
            type: "bar",
            name: "Short OI",
            marker: { color: "red" },
          },
        ]}
        layout={{
          barmode: "group",
          title: "Client-Wise Long vs Short OI Snapshot",
          xaxis: { title: "Client Type" },
          yaxis: { title: "Open Interest" },
        }}
        useResizeHandler
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

const OIAnalysisChart = ({signalData}) => {

  const groupBySignal = () => {
    const signals = {
      'Long Build-up': [],
      'Short Build-up': [],
      'Short Covering': [],
      'Long Unwinding': [],
      'Neutral': []
    };

    signalData.forEach(item => {
      const label = item.option_type === 'XX' ? `${item.symbol} (FUT)` : `${item.strike} ${item.option_type}`;
      signals[item.signal].push(label);
    });

    return signals;
  };

  const grouped = groupBySignal();

  const traces = Object.keys(grouped).map(signal => ({
    type: 'bar',
    name: signal,
    x: grouped[signal],
    y: Array(grouped[signal].length).fill(1), // frequency count for each strike or symbol
    marker: {
      color: {
        'Long Build-up': '#2ca02c',
        'Short Build-up': '#d62728',
        'Short Covering': '#1f77b4',
        'Long Unwinding': '#ff7f0e',
        'Neutral': '#aaaaaa'
      }[signal]
    }
  }));

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Open Interest Signal Analysis</h2>
      <Plot
        data={traces}
        layout={{
          barmode: 'stack',
          title: 'OI Signal Distribution by Strike / Futures',
          xaxis: { title: 'Strike Price / Symbol' },
          yaxis: { title: 'Count (Signals)' },
          height: 500,
        }}
      />
    </div>
  );
};

const FuturesSignalChart = ({ signalData }) => {
  const expiries = signalData.map(d => d.expiry);
  const signalStrength = signalData.map(d => {
    switch (d.signal) {
      case "Long Build-up":
        return 2;
      case "Short Covering":
        return 1;
      case "Long Unwinding":
        return -1;
      case "Short Build-up":
        return -2;
      default:
        return 0;
    }
  });

  return (
    // <div style={styles.container}>
    <div style={styles.chartBox}>
    <Plot
      data={[
        {
          x: expiries,
          y: signalStrength,
          type: "bar",
          marker: {
            color: signalStrength.map(s =>
              s === 2
                ? "green"
                : s === 1
                ? "lightgreen"
                : s === -1
                ? "orange"
                : "red"
            )
          },
          text: signalData.map(d => d.signal),
          textposition: "auto"
        }
      ]}
      layout={{
        title: "Futures OI Signals by Expiry",
        height : 400,
        width : 1000,
        xaxis: { title: "Expiry" },
        yaxis: { title: "Signal Strength", tickvals: [-2, -1, 0, 1, 2] },
        bargap: 0.4
      }}
      style={{ width: '100%', height: '100%' }}
    />
    </div>
    // </div>
  );
};

// Convert signal string to numerical strength
const signalToStrength = (signal) => {
  switch (signal) {
    case "Long Build-up":
    case "Put Writing":
      return 2;
    case "Short Covering":
    case "Call Unwinding":
    case "Put Unwinding":
      return 1;
    case "Long Unwinding":
    case "Call Writing":
      return -1;
    case "Short Build-up":
      return -2;
    default:
      return 0;
  }
};

// Colors for different expiries (add more as needed)
const colorPalette = [
  "#636EFA", "#EF553B", "#00CC96", "#AB63FA", "#FFA15A", "#19D3F3", "#FF6692", "#B6E880"
];

// Group by expiry + option_type
const filterByOI = (optionData) => {
  const grouped = {};

  optionData.forEach((item) => {
    const key = `${item.expiry}_${item.option_type}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });

  const filtered = [];

  Object.keys(grouped).forEach((key) => {
    const items = grouped[key];
    const totalOI = items.reduce((acc, d) => acc + (d.oi || 0), 0);
    const threshold = totalOI * 0.05; // 5% of total OI

    const filteredItems = items.filter((d) => (d.oi || 0) >= threshold);
    filtered.push(...filteredItems);
  });

  return filtered;
};

const OptionsSignalChart = ({ signalData }) => {

  const filteredData = (() => {
  const top3Expiries = [...new Set(signalData.map(d => d.expiry))]
    .sort((a, b) => new Date(a) - new Date(b))
    .slice(0, 3);

  const filteredExpiriesData = signalData.filter(d => top3Expiries.includes(d.expiry));
  return filterByOI(filteredExpiriesData);
  })();

  const expiries = [...new Set(filteredData.map((d) => d.expiry))].sort();
  const strikes = [...new Set(filteredData.map((d) => d.strike))].sort((a, b) => a - b);

  const traces = [];


  expiries.forEach((expiry, expiryIndex) => {
    const ceData = filteredData.filter((d) => d.option_type === "CE" && d.expiry === expiry);
    const peData = filteredData.filter((d) => d.option_type === "PE" && d.expiry === expiry);

    traces.push({
      x: ceData.map((d) => d.strike),
      y: ceData.map((d) => signalToStrength(d.signal)),
      name: `CE ${expiry}`,
      type: "bar",
      marker: { color: colorPalette[expiryIndex % colorPalette.length] },
      text: ceData.map((d) => d.signal),
      textposition: "auto"
    });

    traces.push({
      x: peData.map((d) => d.strike),
      y: peData.map((d) => signalToStrength(d.signal)),
      name: `PE ${expiry}`,
      type: "bar",
      marker: {
        color: colorPalette[(expiryIndex + 1) % colorPalette.length],
        opacity: 0.7
      },
      text: peData.map((d) => d.signal),
      textposition: "auto"
    });
  });

  return (
    <div style={styles.chartBox}>
    <Plot
      data={traces}
      layout={{
        title: "Options OI Signals by Strike & Expiry",
        xaxis: { title: "Strike Price", type: "category" },
        yaxis: {
          title: "Signal Strength",
          tickvals: [-2, -1, 0, 1, 2],
          ticktext: [
            "Short Build-up",
            "Call Writing / Long Unwinding",
            "",
            "Short Covering / Unwinding",
            "Put Writing / Long Build-up"
          ]
        },
        barmode: "group",
        legend: { orientation: "h", y: -0.2 },
        margin: { t: 50, b: 100 }
      }}
        style={{ width: '100%', height: '100%' }}
    />
    </div>
  );
};

const signalColors = {
  "Long Build-up": 'green',
  "Short Build-up": 'red',
  "Short Covering": 'orange',
  "Long Unwinding": 'blue',
};

const OptionsSignalBubbleChart = ({ signalData }) => {
  // Get top 3 expiries

  const filteredData = (() => {
  const top3Expiries = [...new Set(signalData.map(d => d.expiry))]
    .sort((a, b) => new Date(a) - new Date(b))
    .slice(0, 3);

  const filteredExpiriesData = signalData.filter(d => top3Expiries.includes(d.expiry));
  return filterByOI(filteredExpiriesData);
  })();
  //
  // const top3Expiries = [...new Set(signalData.map(d => d.expiry))]
  //   .sort((a, b) => new Date(a) - new Date(b))
  //   .slice(0, 3);
  //
  // const filtered = signalData.filter(d => top3Expiries.includes(d.expiry));

  // Group by signal for separate traces
  const traces = Object.keys(signalColors).map(signal => {
    const points = filteredData.filter(d => d.signal === signal);

    return {
      x: points.map(p => p.strike),
      y: points.map(p => p.expiry),
      text: points.map(p => `${p.option_type} - OI: ${p.oi?.toLocaleString()}`),
      mode: 'markers',
      name: signal,
      marker: {
        size: points.map(p => Math.sqrt(p.oi || 1) / 20), // Adjust for better bubble scaling
        sizemode: 'area',
        color: signalColors[signal],
        opacity: 0.7,
        line: {
          width: 1,
          color: '#333'
        }
      },
      type: 'scatter',
    };
  });

  return (
    <div style={styles.chartBox}>
    <Plot
      data={traces}
      layout={{
        title: 'Options OI Signals (Bubble Chart)',
        xaxis: { title: 'Strike Price', tickangle: -45 },
        yaxis: { title: 'Expiry', type: 'category' },
        showlegend: true,
        height: 600,
        width : 1000,
        hovermode: 'closest',
      }}
      config={{ responsive: true }}
        style={{ width: '100%', height: '100%' }}
    />
    </div>
  );
};

const Commentary = ({ futuresSignals, optionsSignals }) => {
  const commentary = generateMarketCommentary(futuresSignals, optionsSignals);

  return (
    // <div style={styles.container}>
    <div style={styles.chartBox}>
    <div className="bg-gray-50 p-2 rounded-md shadow">
      <h2 className="text-lg font-bold mb-2">📢 Market Commentary</h2>
      <pre className="text-sm-center text-gray-800 whitespace-pre-wrap">{commentary}</pre>
    </div>
    </div>
    // </div>
  );
};

const MarketStructure = () => {
  // const stockData = useContext(StockContext);
  // const [indices, setIndices] = useState([]);
  const subSections = ["Futures/OI Trends", "Max Pain/Option Expiry", "Institutional Activity"];
  const [data, setData] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState(subSections[0]);

  useEffect(() => {
    fetchMarketSructure()
      .then(data => {
        console.log("MKT_STRUCT",data)
        setData(data);
      });
      }, []);

  if (!data) return <p>Loading data...</p>;

  console.log("SUMMARY DATA  RECVD" , data);
  // if (!stockData) return <p>Loading stock data...</p>;

  const renderContent = () => {

    switch (activeSubTab) {
      case "Futures/OI Trends":
        return  data.rolling_spreads.length > 0 ? (<div align={"center"}>
      {/*<IndexCards data={JSON.parse(indices.indices)} />*/}
      <RollingSpreadChart spreads={data.rolling_spreads}/>
      <FuturesSignalChart signalData={data.futures_df}/>
      <OptionsSignalBubbleChart signalData={data.options_df}/>
      <Commentary futuresSignals={data.futures_df} optionsSignals={data.options_df}/>

            </div>) : (
                <p>Loading data...</p>
              );
      case "Max Pain/Option Expiry":
        return  data.rolling_spreads.length > 0 ? (<div align={"center"}>
        <Chartgrid rows={1} cols={1} symbols={['NIFTY']} charttype={['oi']}/>
            </div>) : (
                <p>Loading data...</p>
              );

      case "Institutional Activity":
        return  data.rolling_spreads.length > 0 ? (<div align={"center"}>
      {/*<IndexCards data={JSON.parse(indices.indices)} />*/}
      <ClientLongShortTrendChart chartData={data.clients_trend}/>
      <ClientLongShortBarChart barData={data.clients_latest}/>
            </div>) : (
                <p>Loading data...</p>
              );

      // case "Currency":
      //   return  indices.indices?.length > 0 ? (<div align={"center"}>
      // <ForexPerformanceChart indices={JSON.parse(indices.hist_df)}/>
      //       </div>) : (
      //           <p>Loading data...</p>
      //         );
      // case "Commodities":
      //   return  indices.indices?.length > 0 ? (<div align={"center"}>
      // <CommodityPerformanceChart indices={JSON.parse(indices.hist_df)}/>
      //       </div>) : (
      //           <p>Loading data...</p>
      //         );
      default:
        return <div>Select a tab to view content.</div>;
    }
  };

  return (
    <div className="section-container">
      {/* Vertical Sidebar */}
      <div className="sidebar">
        {subSections.map((tab) => (
          <button
            key={tab}
            className={`side-tab ${activeSubTab === tab ? "active" : ""}`}
            onClick={() => setActiveSubTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content Display */}
      <div className="content">
        {/*<h2>{activeSubTab}</h2>*/}
        {/*<p>Displaying data for {activeSubTab}...</p>*/}
        {renderContent()}
      </div>
    </div>
  );
};

export default MarketStructure;

// ✅ CSS-in-JS (Replacing Tailwind)
const styles = {
  container: {
    display: "flex",
    padding: "16px",
  },
  chartBox: {
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "10px",
    backgroundColor: "white",
     marginBottom: '20px'
  },

  chartContainer: {
    width: "93%",
    paddingRight: "16px",
  },
  sidebar: {
    width: "7%",
    padding: "16px",
    borderLeft: "1px solid #ccc",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  title: {
    fontSize: "1.25rem",
    fontWeight: "bold",
  },
  timeframeContainer: {
    display: "flex",
    gap: "12px",
  },
  timeframeOption: {
    cursor: "pointer",
    color: "#555",
  },
  sidebarTitle: {
    fontSize: "1rem",
    fontWeight: "bold",
    marginBottom: "8px",
  },
  tickerList: {
    listStyleType: "none",
    padding: 0,
    margin: 0,
  },
  tickerItem: {
    cursor: "pointer",
    color: "#555",
    padding: "4px 0",
    borderBottom: "1px dashed #ccc",
    transition: "color 0.2s",
  },
};